import React, { useEffect, useRef, useState } from 'react';

import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Button from '@mui/material/Button';
import { Slider, Stack, Tooltip, Typography } from '@mui/material';

import { Replay } from '@mui/icons-material';

import { COLORS } from '../../../utils/colors';
import { useEditorStyles } from './styles';

import { useAIDocumentEditorContext } from '../../../contexts/AIDocumentEditorContext';
import { useSessionContext } from '../../../contexts/SessionContext';
import toggleToast from '../../../utils/messaging';

import DownIcon from '../../../icons/DownIcon';
import AutoSaveIcon from '../../../icons/StreetIcon';
import DocumentEditorSections from './DocumentEditorSections';
import EditorToolbar from './EditorToolbar';
import { CanvasView } from './ImageEditor';
import DocumentRenderer from './DocumentRenderer';
import { IPromptCard } from '@homeporter/types';
import { useBookingsContext } from '../../../contexts/BookingsContext';
import DownloadIcon from '../../../icons/DownloadIcon';
import SaveIcon from '../../../icons/SaveIcon';

import PrintPreviewModal from './PrintPreviewModal';

const DocumentWorkspace = () => {
  const { selectedImage } = useSessionContext();
  const [isAutoSaving, setIsAutoSaving] = useState(false);
  const [hasOverflowY, setHasOverflowY] = useState(false);
  const [isAtBottom, setIsAtBottom] = useState(false);
  const scrollableRef = useRef<HTMLDivElement | null>(null);

  const { selectedBooking } = useBookingsContext();

  const {
    documentRef,
    documentSection,
    saveEnabled,
    setSaveEnabled,
    isFetchingDocumentContents,
    errorFetchingDocumentContents,
    saveChanges,
    selectedCard,
    isGeneratingResponse,
    isResettingDocumentContents,
    errorResettingDocumentContents,
  } = useAIDocumentEditorContext();

  const [value, setValue] = useState<number>(100);

  const handleChange = (_event: Event, newValue: number | number[]) => {
    setValue(newValue as number);
  };

  const styles = useEditorStyles();

  const scrollToBottom = (index: number = 0) => {
    const scrollElement = scrollableRef.current;
    const selectedElement = document.getElementById(`card_index_${index}`);

    if (!selectedElement || !scrollElement) return;

    const scroll =
      selectedElement.offsetTop +
      selectedElement.offsetHeight -
      scrollElement.offsetHeight;

    scrollElement.style.scrollBehavior = 'smooth';
    scrollElement.scrollTop = scroll > 0 ? scroll : 0;
  };

  useEffect(() => {
    if (!selectedCard?.obj) return;

    window.setTimeout(
      () => scrollToBottom(selectedCard.index),
      isGeneratingResponse ? 0 : 50,
    );
  }, [(selectedCard?.obj as IPromptCard)?.response]);

  useEffect(() => {
    const checkOverflowY = () => {
      if (scrollableRef.current) {
        setHasOverflowY(
          scrollableRef.current.scrollHeight >
          scrollableRef.current.clientHeight,
        );
      }
    };

    const handleScroll = () => {
      if (scrollableRef.current) {
        const { scrollTop, scrollHeight, clientHeight } = scrollableRef.current;
        setIsAtBottom(scrollTop + clientHeight >= scrollHeight - 10);
      }
    };

    checkOverflowY();
    if (scrollableRef.current) {
      scrollableRef.current.addEventListener('scroll', handleScroll);
    }

    const resizeObserver = new ResizeObserver(() => {
      resizeObserver.disconnect(); // pause
      requestAnimationFrame(() => {
        checkOverflowY();
        if (scrollableRef.current)
          resizeObserver.observe(scrollableRef.current);
      });
    });

    if (scrollableRef.current) {
      resizeObserver.observe(scrollableRef.current);
    }

    return () => {
      if (scrollableRef.current) {
        scrollableRef.current.removeEventListener('scroll', handleScroll);
        resizeObserver.unobserve(scrollableRef.current);
      }
    };
  }, [scrollableRef]);

  const [open, setOpen] = React.useState(false);
  const handleOpenPreview = () => {
    if (!documentRef.current) {
      toggleToast('Nothing to print yet.', 'error');
      return;
    }
    setOpen(true);
  };

  return (
    <>
      {open && documentRef.current && (
        <PrintPreviewModal
          open={open}
          onClose={() => setOpen(false)}
          title={`PropVu Report – ${selectedBooking?.title ?? 'Untitled Report'}`}
          sourceElem={documentRef.current}
          sourceHTML={documentRef.current?.outerHTML}
          pageSize="A4"
        />
      )}

      {selectedImage && <CanvasView />}

      {!selectedImage && (
        <Box
          id="_editor_canvas_top"
          sx={{ display: 'block', marginRight: '1rem' }}>
          {false && (
            <Button
              sx={styles.scrollButton}
              onClick={(e) => {
                e.stopPropagation();
                scrollToBottom(selectedCard?.index);
              }}>
              Scroll
            </Button>
          )}

          <Box sx={styles.canvasTools}>
            {documentSection?.cards.some(
              (item) =>
                ['Prompt', 'TextBlock'].includes(item.type) &&
                (item as IPromptCard).response,
            ) && <EditorToolbar id="0" />}

            {isAutoSaving && (
              <Box sx={styles.sidebarAutoSave}>
                <Box sx={styles.autoSave}>
                  <Typography marginRight={'0.3rem'}>AutoSaving...</Typography>
                  <AutoSaveIcon />
                </Box>
              </Box>
            )}
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '1rem',
                mr: '1rem',
              }}>
              {saveEnabled && (
                <Box
                  sx={{
                    ...styles.sidebarHangUp,
                    backgroundColor: saveEnabled
                      ? COLORS.PRIMARY
                      : COLORS.LIGHT_GRAY,
                  }}>
                  <Tooltip title="Save Document">
                    <IconButton
                      onClick={() => {
                        saveChanges();
                        setSaveEnabled(false);
                        toggleToast('Document Saved', 'success');
                      }}
                      disabled={!saveEnabled}>
                      <SaveIcon
                        strokeWidth={2}
                        sx={{ color: 'white', fontSize: '1.5rem' }}
                      />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}
              <Tooltip title="Download / Print Preview">
                <IconButton
                  sx={styles.downloadIcon}
                  onClick={handleOpenPreview}>
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>

          <Box id="document-editor-workspace" display={'flex'}>
            <DocumentEditorSections />

            <div ref={scrollableRef} style={styles.page as React.CSSProperties}>
              {isFetchingDocumentContents ||
                errorFetchingDocumentContents ||
                isResettingDocumentContents ||
                errorResettingDocumentContents ? (
                <Box sx={styles.pageStatus}>
                  {errorFetchingDocumentContents ||
                    errorResettingDocumentContents ? (
                    <b>Error !</b>
                  ) : (
                    <CircularProgress sx={{ color: COLORS.PRIMARY }} />
                  )}
                </Box>
              ) : (
                <>
                  {hasOverflowY && !isAtBottom && (
                    <Box sx={styles.scrollDown}>
                      <DownIcon />
                    </Box>
                  )}
                  <div
                    style={{
                      ...styles.pages,
                      transform: `scale(${value / 100})`,
                    }}>
                    <DocumentRenderer />
                  </div>
                </>
              )}
              {value !== 100 && (
                <IconButton sx={styles.zoomIcon} onClick={() => setValue(100)}>
                  <Replay fontSize="small" />
                </IconButton>
              )}
              <Box
                position={'absolute'}
                right={30}
                bottom={10}
                sx={styles.zoomSlider}>
                <Stack spacing={2} direction="row" sx={styles.zoomSlider2}>
                  <Slider
                    defaultValue={100}
                    value={value}
                    min={0}
                    max={200}
                    step={1}
                    onChange={handleChange}
                    sx={styles.zoomSlider3}
                  />
                  <span style={{ fontSize: '0.8rem' }}>{value}%</span>
                </Stack>
              </Box>
            </div>
          </Box>
        </Box>
      )}
    </>
  );
};

export default DocumentWorkspace;
